/**
 * Ресурсный модуль analytics (браузер → /api/analytics). Слой L2.
 *
 * Чистые типизированные функции поверх http-ядра, без React. Хуки
 * (@/hooks/useAnalytics) зовут этот модуль; компоненты — только хуки.
 * Эталон структуры — cases.api.ts (см. RULES.md).
 */

import { http } from '@/lib/api/http'
import { buildModuleBase } from '@/lib/api/module-path'

const BASE = buildModuleBase('analytics')

// ── Параметры запросов (форма провода). Живут рядом с ресурсом. ──────────────

export interface AnomaliesParams {
  status?: string
  scenarioId?: number
  method?: string
}

export interface ForecastParams {
  scenarioId?: number
  type?: string
  horizonDays?: number
}

export interface RecommendationsParams {
  status?: string
}

export interface GenerateRecommendationsParams {
  scenarioId?: number
}

export interface RecommendationFeedbackBody {
  recId: string
  feedback: string
}

export interface SchemesParams {
  type?: string
  minFrequency?: number
  patternId?: string
}

export interface GraphParams {
  scenarioId?: number
}

export interface GraphClustersParams {
  scenarioId?: number
  limit?: number
}

export interface GraphFeaturesParams {
  bin: string
}

// ── Graph DTO (форма провода для /api/analytics/graph*) ──────────────────────

export interface GraphAnalyzeNode {
  node_id: string
  label: string
  node_type: string
  pagerank?: number
  betweenness?: number
  degree?: number
}

export interface GraphCluster {
  cluster_id: number
  size: number
  risk_score: number | null
  members?: GraphAnalyzeNode[]
}

export interface GraphAnalyzeResponse {
  scenario_id: number
  total_nodes: number
  total_edges: number
  cluster_count: number
  clusters?: GraphCluster[]
}

export interface GraphClustersListItem {
  cluster_id: number
  scenario_id: number
  size: number
  risk_score: number | null
  top_members?: Array<{ bin?: string; name?: string; pagerank?: number }>
}

export interface GraphClustersListResponse {
  total: number
  clusters: GraphClustersListItem[]
}

export interface GraphFeaturesResponse {
  bin: string
  name: string
  pagerank: number
  betweenness: number
  degree: number
  communityId: number
  communitySize: number
  triangles: number
  outgoingMatches: number
  incomingMatches: number
  connectedCompanies: string[]
}

// ── Recommendations DTO ──────────────────────────────────────────────────────

export interface RecommendationItem {
  id: string
  priority: 1 | 2 | 3
  title: string
  description: string
  entityType: string
  entityId: string
  suggestedAction: string
  status: string
}

/**
 * Роут /api/analytics/recommendations отдаёт либо объект
 * `{ recommendations, source }` (источник БД), либо «голый» массив (ML-сервис).
 * Нормализация обоих вариантов — в хуке useRecommendationsAnalytics.
 */
export type RecommendationsResponse =
  | { recommendations: RecommendationItem[]; source?: 'db' | 'ml' }
  | RecommendationItem[]

// ── Типы ответов (DTO). Форма данных, читаемая компонентами. ─────────────────

export interface KpiItem {
  name: string
  value: number
  target: number
  trend: string
  period: string
}

export interface KpiResponse {
  kpis: KpiItem[]
}

export interface AnomalyItem {
  caseId: number
  anomalyScore: number
  deviations: Record<string, number>
}

export interface AnomaliesResponse {
  anomalies: AnomalyItem[]
  totalCases: number
  anomalyCount: number
  method: string
}

export interface ForecastPoint {
  date: string
  value: number
  lowerBound: number
  upperBound: number
}

export interface ForecastTrend {
  scenarioId: number
  recentCount: number
  changePct: number
  trend: string
}

export interface ForecastResponse {
  points?: ForecastPoint[]
  trends?: ForecastTrend[]
}

export interface CargoRiskProfile {
  cargoName: string
  riskScore: number
  caseCount: number
}

export interface CargoSubstitution {
  wagonNumber: string
  importCargo: string
  exportCargo: string
  exportDate: string
}

export interface CargoFlow {
  cargoName: string
  importCountry: string
  exportCountry: string
  volume: number
}

export interface CargoRoute {
  importStation: string
  exportStation: string
  usageCount: number
  uniqueWagons: number
}

export interface CargoResponse {
  profiles?: CargoRiskProfile[]
  substitutions?: CargoSubstitution[]
  flows?: CargoFlow[]
  routes?: CargoRoute[]
}

export interface FinancialMetric {
  period: string
  totalPotentialLoss: number
  recoveredAmount: number
  pendingAmount: number
  caseCount: number
  avgLossPerCase: number
}

export interface FinancialRegion {
  region: string
  caseCount: number
  riskScore: number
  financialImpact: number
}

export interface FinancialResponse {
  metrics?: FinancialMetric[]
  regions?: FinancialRegion[]
}

export const analyticsApi = {
  /** Аномалии. signal прокидывается из React Query. */
  getAnomalies: (params?: AnomaliesParams, opts?: { signal?: AbortSignal }) =>
    http.get<AnomaliesResponse>(`${BASE}/anomalies`, {
      params: {
        status: params?.status,
        scenarioId: params?.scenarioId,
        method: params?.method,
      },
      signal: opts?.signal,
    }),

  /** Прогноз. */
  getForecast: (params?: ForecastParams, opts?: { signal?: AbortSignal }) =>
    http.get<ForecastResponse>(`${BASE}/forecast`, {
      params: {
        scenarioId: params?.scenarioId,
        type: params?.type,
        horizonDays: params?.horizonDays,
      },
      signal: opts?.signal,
    }),

  /** Грузовая аналитика. */
  getCargo: (type: string = 'risk', opts?: { signal?: AbortSignal }) =>
    http.get<CargoResponse>(`${BASE}/cargo`, { params: { type }, signal: opts?.signal }),

  /** Финансовые метрики. */
  getFinancial: (type: string = 'risk', opts?: { signal?: AbortSignal }) =>
    http.get<FinancialResponse>(`${BASE}/financial`, { params: { type }, signal: opts?.signal }),

  /** KPI. */
  getKpi: (opts?: { signal?: AbortSignal }) =>
    http.get<KpiResponse>(`${BASE}/kpi`, { signal: opts?.signal }),

  /** Рекомендации (список). Может вернуть объект или «голый» массив. */
  getRecommendations: (params?: RecommendationsParams, opts?: { signal?: AbortSignal }) =>
    http.get<RecommendationsResponse>(`${BASE}/recommendations`, {
      params: { status: params?.status },
      signal: opts?.signal,
    }),

  // ── Мутации ───────────────────────────────────────────────────────────────

  /** Генерация рекомендаций. */
  generateRecommendations: (params?: GenerateRecommendationsParams) =>
    http.post(`${BASE}/recommendations`, params || {}),

  /** Отзыв по рекомендации. */
  recommendationFeedback: (body: RecommendationFeedbackBody) =>
    http.post(`${BASE}/recommendations`, body),

  /** Схемы. */
  getSchemes: (params?: SchemesParams, opts?: { signal?: AbortSignal }) =>
    http.get(`${BASE}/schemes`, {
      params: {
        type: params?.type,
        minFrequency: params?.minFrequency,
        patternId: params?.patternId,
      },
      signal: opts?.signal,
    }),

  /** Данные графа (analyze): узлы, рёбра, кластеры по сценарию. */
  getGraph: (params?: GraphParams, opts?: { signal?: AbortSignal }) =>
    http.get<GraphAnalyzeResponse>(`${BASE}/graph`, {
      params: { scenarioId: params?.scenarioId },
      signal: opts?.signal,
    }),

  /** Кластеры графа (список с лимитом). */
  getGraphClusters: (params?: GraphClustersParams, opts?: { signal?: AbortSignal }) =>
    http.get<GraphClustersListResponse>(`${BASE}/graph/clusters`, {
      params: { scenarioId: params?.scenarioId, limit: params?.limit },
      signal: opts?.signal,
    }),

  /** Граф-фичи компании по БИН. */
  getGraphFeatures: (params: GraphFeaturesParams, opts?: { signal?: AbortSignal }) =>
    http.get<GraphFeaturesResponse>(`${BASE}/graph/features`, {
      params: { bin: params.bin },
      signal: opts?.signal,
    }),
}
